import * as React from "react";
import StartIcon from "@mui/icons-material/Start";
import LoadingModal from "../components/modals/LoadingModal";
import NotificationSnackbar from "../components/NotificationSnackbar";
import CompassNameIcon from "../images/general/CgiNameIconBlack.svg";
import CompassLogoGradient from "../images/general/CompassIconGradient.svg";
import HexagonsLeft from "../images/home/hexagons/HexagonalsLeft.png";
import HexagonsRight from "../images/home/hexagons/HexagonalsRight.png";
import SpecialText from "../components/SpecialText";
import ThankYouScreen from "../components/ThankYouScreen";
import CustomerPreferences from "../components/CustomerPreferences";
import PublicAssessment from "../components/PublicAssessment";
import PublicAssessmentResults from "./PublicAssessmentResults";
import { post } from "@aws-amplify/api";
import {
  createPublicAssessmentSubmission,
  fetchPublicAssessmentById,
} from "../helpers/apiRequests";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { languages } from "../helpers/enums";
import { v4 as uuid } from "uuid";
import {
  Box,
  Button,
  Checkbox,
  Container,
  Icon,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

export default function PublicAssessments() {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [assessmentData, setAssessmentData] = React.useState("");

  async function fetchAssessmentData() {
    if (!assessmentId) return;
    setOpenLoading(true);
    try {
      const fetchedPublicAssessment = await fetchPublicAssessmentById(
        assessmentId,
        { authMode: "apiKey" },
      );
      if (
        !fetchedPublicAssessment ||
        !fetchedPublicAssessment.public ||
        !fetchedPublicAssessment.active
      ) {
        navigate("/");
        setOpenLoading(false);
        return;
      }
      setAssessmentData(fetchedPublicAssessment);
    } catch (e) {
      console.error(e);
      setNotificationMessage(t("errorFetchDataMessage"));
      setOpenNotification(true);
    }
    setOpenLoading(false);
  }

  React.useEffect(() => {
    fetchAssessmentData();
  }, []);

  const [openNotification, setOpenNotification] = React.useState(false);
  const [notificationMessage, setNotificationMessage] = React.useState("");

  const [openLoading, setOpenLoading] = React.useState(false);

  const [nameError, setNameError] = React.useState(false);
  const [mailError, setMailError] = React.useState(false);

  const isValidEmail = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g;

  const inputWidth = 300;
  const inputMargin = 3;

  function markQuestionAsVisited(questionId) {
    if (!answerData?.visitedQuestions?.includes(questionId)) {
      setAnswerData((prev) => ({
        ...prev,
        visitedQuestions: [...answerData.visitedQuestions, questionId],
      }));
    }
  }

  function handleAnswerDataChange(event, index) {
    setAnswerData((prev) => ({
      ...prev,
      [index]: event.target.value,
    }));
  }

  function setNewPage(value) {
    setAnswerData((prev) => ({
      ...prev,
      page: value,
    }));
  }

  function setNewCustomerPreferences(value) {
    setAnswerData((prev) => ({
      ...prev,
      customerPreferences: value,
    }));
  }

  function setNewAssessmentAnswers(requirementKey, valueObject) {
    setAnswerData((prev) => ({
      ...prev,
      assessmentAnswers: {
        ...prev.assessmentAnswers,
        [requirementKey]: valueObject,
      },
    }));
  }

  const emptyAnswerData = {
    page: 0,
    customerName: "",
    customerPhone: "",
    customerMail: "",
    customerOrganization: "",
    consentChecked: false,
    consentMailsChecked: false,
    customerPreferences: [],
    assessmentAnswers: {},
    visitedQuestions: [],
  };

  const [answerData, setAnswerData] = React.useState(() => {
    // getting stored value
    const saved = localStorage.getItem(assessmentId);
    const initialValue = JSON.parse(saved);
    return initialValue || emptyAnswerData;
  });

  React.useEffect(() => {
    localStorage.setItem(assessmentId, JSON.stringify(answerData));
  }, [answerData]);

  if (assessmentData === "") {
    return <></>;
  }

  function createEvaluationData() {
    const assessmentModules = assessmentData?.assessmentModules
      ? JSON.parse(assessmentData.assessmentModules)
      : {};
    const assessmentDataWithResults = {
      ...assessmentData,
      assessmentModules,
    };
    const recommendations = [];

    Object.values(assessmentDataWithResults.assessmentModules).forEach(
      (module) => {
        let maxModuleScore = 0;
        let moduleScore = 0;
        let moduleDisabled = true;

        Object.values(module.questions || {}).forEach((question) => {
          let maxQuestionScore = 0;
          let questionScore = 0;
          let questionDisabled = true;

          Object.values(question.requirements || {}).forEach((requirement) => {
            const answer = answerData.assessmentAnswers?.[requirement.id];
            if (answer) {
              requirement.disabled = !!answer.disabled;
              if (!answer.disabled) {
                requirement.score = answer.score;
                if (answer.score <= 1) {
                  recommendations.push({
                    requirement: requirement.text,
                    recommendation: requirement.solution,
                  });
                }

                questionScore += answer.score * requirement.weight;
                maxQuestionScore += requirement.weight * 3;
                questionDisabled = false;

                moduleScore += answer.score * requirement.weight;
                maxModuleScore += requirement.weight * 3;
                moduleDisabled = false;
              }
            } else {
              requirement.disabled = false;
              requirement.score = 0;
              recommendations.push({
                requirement: requirement.text,
                recommendation: requirement.solution,
              });

              maxQuestionScore += requirement.weight * 3;
              questionDisabled = false;

              maxModuleScore += requirement.weight * 3;
              moduleDisabled = false;
            }
          });

          question.maxScore = maxQuestionScore;
          question.score = questionScore;
          question.questionDisabled = questionDisabled;
        });

        module.maxScore = maxModuleScore;
        module.score = moduleScore;
        module.moduleDisabled = moduleDisabled;
      },
    );

    assessmentDataWithResults.recommendations = recommendations;
    assessmentDataWithResults.interestedIn = answerData.customerPreferences;
    return assessmentDataWithResults;
  }

  function createMailEvaluationData(evaluationData) {
    const assessmentModules = Object.fromEntries(
      Object.entries(evaluationData.assessmentModules || {}).map(
        ([moduleId, module]) => [
          moduleId,
          {
            id: module.id,
            title: module.title || module.name,
            name: module.name,
            score: module.score,
            maxScore: module.maxScore,
            moduleDisabled: module.moduleDisabled,
          },
        ],
      ),
    );

    return {
      id: evaluationData.id,
      title: evaluationData.title || evaluationData.name,
      assessmentModules,
      recommendations: evaluationData.recommendations || [],
      interestedIn: evaluationData.interestedIn || [],
    };
  }

  async function finishAssessment() {
    setOpenLoading(true);
    let failedStep = "finishAssessment";
    try {
      failedStep = "createEvaluationData";
      const evaluationData = createEvaluationData();
      const mailEvaluationData = createMailEvaluationData(evaluationData);

      failedStep = "createPublicAssessmentSubmission";
      const submissionId = uuid();
      const submission = await createPublicAssessmentSubmission({
        id: submissionId,
        assessmentID: assessmentId,
        customerName: answerData.customerName,
        customerMail: answerData.customerMail,
        customerPhone: answerData.customerPhone,
        customerOrganization: answerData.customerOrganization,
        consentMarketingMails: answerData.consentMailsChecked,
        preferences: answerData.customerPreferences,
        answerData: JSON.stringify(answerData.assessmentAnswers),
        ownerGroups: assessmentData.ownerGroups || [],
      });

      const apiName = "CreatePublicEvaluationApi";
      const path = "/createPublicEvaluation";
      const options = {
        body: {
          submissionId: submission.id,
          customerName: answerData.customerName,
          customerMail: answerData.customerMail,
          assessmentTitle: assessmentData.title,
          evaluationData: mailEvaluationData,
          language: languages.EN,
        },
      };

      failedStep = "sendPublicEvaluationMail";
      await post({ apiName, path, options }).response;

      setAnswerData((prev) => ({
        ...prev,
        customerId: submission.id,
        evaluationData,
      }));
      setNewPage(3);
    } catch (error) {
      console.error(`finishAssessment failed at ${failedStep}`, error);
      setNotificationMessage(t("errorMessage"));
      setOpenNotification(true);
    } finally {
      setOpenLoading(false);
    }
  }

  async function postCustomerData() {
    try {
      setAnswerData((prev) => ({
        ...prev,
        customerId: prev.customerId || uuid(),
      }));
      setNewPage(1);
    } catch (e) {
      setNotificationMessage(t("errorMessage"));
      setOpenNotification(true);
    }
  }

  return (
    <>
      {/* <Button onClick={() => createPublicEvaluation()}>fwefw</Button> */}
      <Box
        sx={{
          display: "flex",
          width: 1,
          flexDirection: "row",
          justifyContent: "flex-end",
          mb: -4,
          pt: 4,
          zIndex: -10,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            mr: 5,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => setAnswerData(emptyAnswerData)}
            sx={{
              textTransform: "none",
              borderRadius: 20,
              mr: -2,
              mb: 1,
              py: 0,
              zIndex: 10,
            }}
          >
            {t("resetAssessment")}
          </Button>
          {answerData.page > 0 && (
            <>
              <Typography align="right">{answerData.customerName}</Typography>
              <Typography align="right">{answerData.customerMail}</Typography>
              {answerData?.customerPhone && (
                <Typography align="right">
                  {answerData.customerPhone}
                </Typography>
              )}
              {answerData?.customerOrganization && (
                <Typography align="right">
                  {answerData.customerOrganization}
                </Typography>
              )}
            </>
          )}
          {answerData?.customerPreferences.length !== 0 &&
            answerData.page > 1 && (
              <Typography align="right">
                {t("interestedIn:")} {answerData.customerPreferences.join(", ")}
              </Typography>
            )}
        </Box>
      </Box>

      <Container
        maxWidth="lg"
        sx={{ width: 1, px: "0 !important", position: "relative" }}
      >
        {answerData?.page === 0 && (
          <>
            <Box
              component="img"
              draggable={false}
              src={HexagonsLeft}
              sx={{
                display: { xs: "none", lg: "block" },
                maxWidth: 300,
                width: "auto",
                height: "auto",
                objectFit: "contain",
                position: "absolute",
                top: 200,
                left: 0,
                zIndex: 100,
                userSelect: "none",
              }}
            />
            <Box
              component="img"
              draggable={false}
              src={HexagonsRight}
              sx={{
                display: { xs: "none", lg: "block" },
                maxWidth: 300,
                width: "auto",
                height: "auto",
                objectFit: "contain",
                position: "absolute",
                top: 210,
                right: 0,
                zIndex: 100,
                userSelect: "none",
              }}
            />
          </>
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mx: 1,
          }}
        >
          {answerData.page === 0 && (
            <>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  mt: 10,
                }}
              >
                <Icon sx={{ width: "auto", height: "auto" }}>
                  <img
                    draggable={false}
                    src={CompassLogoGradient}
                    height={"55px"}
                  />
                </Icon>
                <Icon sx={{ width: "auto", height: "auto", ml: 2 }}>
                  <img draggable={false} src={CompassNameIcon} height="50px" />
                </Icon>
              </Box>
              <Typography
                align="center"
                sx={{ fontSize: 25, mt: 7, width: 500, maxWidth: 1 }}
              >
                {t("welcomeToAssessment")}
                <SpecialText
                  style={{
                    background:
                      "-webkit-linear-gradient(45deg, #E31937, #A82465 60%, #5236AB)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  {" "}
                  "{assessmentData.title}"
                </SpecialText>
              </Typography>
              <Typography sx={{ fontSize: 20, mt: 4 }}>
                Please enter your contact data first.
              </Typography>
              <TextField
                InputLabelProps={{
                  shrink: true,
                }}
                error={nameError}
                helperText={nameError ? t("pleaseEnterYourName") : false}
                label={t("name")}
                variant="outlined"
                size="small"
                sx={{ width: inputWidth, maxWidth: 1, mt: 7 }}
                value={answerData.customerName}
                onChange={(event) => {
                  if (event.target.value !== "") {
                    setNameError(false);
                  }
                  handleAnswerDataChange(event, "customerName");
                }}
              />
              <TextField
                InputLabelProps={{
                  shrink: true,
                }}
                error={mailError}
                helperText={mailError ? t("pleaseEnterValidMail") : false}
                label={t("mail")}
                variant="outlined"
                size="small"
                sx={{ width: inputWidth, maxWidth: 1, mt: inputMargin }}
                value={answerData.customerMail}
                onChange={(event) => {
                  if (event.target.value.match(isValidEmail)) {
                    setMailError(false);
                  }
                  handleAnswerDataChange(event, "customerMail");
                }}
              />
              <TextField
                InputLabelProps={{
                  shrink: true,
                }}
                label={t("phoneOptional")}
                variant="outlined"
                size="small"
                sx={{ width: inputWidth, maxWidth: 1, mt: inputMargin }}
                value={answerData.customerPhone}
                onChange={(event) =>
                  handleAnswerDataChange(event, "customerPhone")
                }
              />
              <TextField
                InputLabelProps={{
                  shrink: true,
                }}
                label={t("organisationOptional")}
                variant="outlined"
                size="small"
                sx={{ width: inputWidth, maxWidth: 1, mt: inputMargin }}
                value={answerData.customerOrganization}
                onChange={(event) =>
                  handleAnswerDataChange(event, "customerOrganization")
                }
              />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignContent: "center",
                  justifyContent: "center",
                  width: inputWidth,
                  maxWidth: 1,
                  userSelect: "none",
                  mt: 3,
                }}
              >
                <Checkbox
                  checked={answerData.consentChecked}
                  onChange={(e) =>
                    setAnswerData((prev) => ({
                      ...prev,
                      consentChecked: e.target.checked,
                    }))
                  }
                />
                <Tooltip title="Ich willige ein, dass meine angegebenen personenbezogenen Daten (z.B. Name, E-Mail-Adresse, Position) für die Durchführung und Auswertung des Assessments durch CGI Compass verarbeitet und genutzt werden. Diese Einwilligung umfasst die unmittelbar mit dem ausgewählten Assessment in Verbindung stehende Kontaktaufnahme durch CGI. Diese Einwilligung kann jederzeit mit Wirkung für die Zukunft widerrufen werden.">
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontFamily: "Inter",
                      m: "auto",
                      "&:hover": {
                        cursor: "pointer",
                      },
                    }}
                  >
                    Einwilligungserklärung für "Nutzung von CGI Compass
                    basierend auf Ihrer Einwilligung"
                  </Typography>
                </Tooltip>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignContent: "center",
                  justifyContent: "center",
                  width: inputWidth,
                  maxWidth: 1,
                  userSelect: "none",
                  mt: 1,
                }}
              >
                <Checkbox
                  checked={answerData.consentMailsChecked}
                  onChange={(e) =>
                    setAnswerData((prev) => ({
                      ...prev,
                      consentMailsChecked: e.target.checked,
                    }))
                  }
                />
                <Tooltip title="Ich willige ein, dass meine personenbezogenen Daten für den Versand von Informationsmaterialien zum CGI Compass Portfolio genutzt werden dürfen, um mich über relevante Entwicklungen und Angebote zu informieren. Diese Einwilligung kann jederzeit mit Wirkung für die Zukunft widerrufen werden.">
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontFamily: "Inter",
                      m: "auto",
                      "&:hover": {
                        cursor: "pointer",
                      },
                    }}
                  >
                    Optional: Einwilligungserklärung für den regelmäßigen Erhalt
                    von Informationsmaterialien
                  </Typography>
                </Tooltip>
              </Box>
              {/* <Button
              variant="contained"
              sx={{ borderRadius: 20, mt: 5 }}
              onClick={() => {
                testMailInvocation();
              }}
            >
              {"test Mail"}
            </Button> */}
              <Button
                variant="contained"
                disabled={!answerData.consentChecked || mailError || nameError}
                sx={{ borderRadius: 20, mt: 5 }}
                endIcon={<StartIcon />}
                onClick={() => {
                  if (!answerData.customerMail.match(isValidEmail)) {
                    setMailError(true);
                  }
                  if (answerData.customerName === "") {
                    setNameError(true);
                  }
                  if (
                    !answerData.customerMail.match(isValidEmail) ||
                    answerData.customerName === ""
                  ) {
                    return;
                  }
                  postCustomerData();
                }}
              >
                Start Assessment
              </Button>
            </>
          )}
          {answerData.page === 1 && (
            <CustomerPreferences
              setPage={setNewPage}
              selectedPreferences={answerData.customerPreferences}
              setSelectedPreferences={setNewCustomerPreferences}
            />
          )}
          {answerData.page === 2 && (
            <PublicAssessment
              finishAssessment={finishAssessment}
              assessmentData={assessmentData}
              assessmentAnswerData={answerData.assessmentAnswers}
              visitedQuestions={answerData.visitedQuestions}
              setNewAssessmentAnswers={setNewAssessmentAnswers}
              markQuestionAsVisited={markQuestionAsVisited}
            />
          )}
          {answerData.page === 3 && (
            <PublicAssessmentResults
              evaluationData={answerData.evaluationData}
              exit={() => setNewPage(4)}
            />
          )}
          {answerData.page === 4 && <ThankYouScreen />}
        </Box>
      </Container>
      <LoadingModal open={openLoading} />
      <NotificationSnackbar
        message={notificationMessage}
        open={openNotification}
        setOpen={setOpenNotification}
      />
    </>
  );
}
